// traceSemanticFeed.js — the semantic reading of ONE trace: every X-ray
// extractor run over a single request's turns, normalized into one
// turn-ordered feed of typed work operations.
//
// This is the inverse axis of the scope X-ray tabs: those run ONE extractor
// over MANY traces and aggregate; this runs ALL extractors over ONE trace and
// preserves order. The unit changes from request/turn to OPERATION — the
// simplification is re-bucketing, never summarization: everything here is the
// tracers' deterministic output, and every tool call the extractors do NOT
// claim stays visible as residual (the per-trace illegibility gauge), so the
// feed can't silently read as "covered everything" when it didn't.
//
// Position contract: extractors key ops on the tool_use block id (multi-draft
// blocks suffix "#i"), so ops map back to their (turn, block) coordinates
// without touching any tracer. Every tool_use is guaranteed to HAVE an id —
// withToolUseIds mints a positional one for the providers that ship without
// (see its note) — so a tool-call op always resolves to a real position. The
// remaining null-position case is the command door: a slash-command skill
// invocation exists only as text, and its marker positions zip by order, so an
// unmatched one sorts to the end with turnIndex null.

import { xrayRegistry } from "./xrayRegistry.js";
import { listBehaviourArtifacts } from "./behaviourArtifactStore.js";
import { extractFileOps } from "./traceFileTree.js";
import { extractDerivedFileOps } from "./traceDerivedFileOps.js";
import { extractShellWriteOps } from "./traceShellOrigins.js";
import { extractWebResearchOps } from "./traceWebResearch.js";
import { extractForgeOps } from "./traceForgeOps.js";
import { extractLinearOps } from "./traceLinearOps.js";
import { extractDataQueryOps, extractGrafanaOps } from "./traceQueryOps.js";
import { extractRecallOps } from "./traceRecallOps.js";
import { extractSkillSignals, commandInvocationsOf } from "./traceSkillRuns.js";
import { extractArtifactSignals } from "./traceArtifacts.js";
import { extractCodeCellOps, fallbackCodeCellOps } from "./traceCodeModeCells.js";

// Labels match the scope-level X-ray tabs so the two axes read as one system.
export const FEED_DOMAINS = {
  prompt: { label: "Prompt" },
  reply: { label: "Reply" },
  files: { label: "Files" },
  research: { label: "Research" },
  forge: { label: "Git" },
  linear: { label: "Linear" },
  grafana: { label: "Grafana" },
  query: { label: "Queries" },
  // Version-control work and build/test runs are their own subjects. Both
  // arrive through the shell door (traceShellWork) rather than from a
  // committed extractor, but a domain needs a name wherever its ops come from
  // - a lens with no entry here does not go missing, it shows up as
  // unclassified residual, which reads as the feed failing.
  vcs: { label: "Version control" },
  build: { label: "Builds" },
  recall: { label: "Recall" },
  skill: { label: "Skills" },
  artifact: { label: "Artifacts" },
  code: { label: "Code cells" },
  other: { label: "Unclassified" },
};

// Manifest-declared lenses contribute a domain and an extractor each, replacing
// what used to be a hand-written import plus a hand-written loop per lens. The
// per-trace axis is where omission is worst: a lens with no domain here does not
// go missing, its work shows up as unclassified residual — which reads as the
// feed failing rather than the lens being absent.
// Resolved on first feed build, not at module load: the catalog is tenant-scoped
// and the tenant is unknown while this module imports. Merged into the label table
// on the way past so a runtime lens's domain gets a name rather than rendering as
// its raw id.
export function feedDomains() {
  const merged = { ...FEED_DOMAINS };
  for (const [domain, meta] of Object.entries(xrayRegistry().feedDomains())) {
    if (!merged[domain]) merged[domain] = meta;
  }
  // Behaviour artifacts are feed citizens too — executable lenses whose ops
  // arrive asynchronously through `extraOps` rather than through a registry
  // extractor, but whose domains need names exactly the same way.
  for (const a of listBehaviourArtifacts()) {
    if (merged[a.behaviour]) {
      // Shadowing is the failure the registry's mergeInto() exists to surface,
      // and it applies here identically: the registry's label wins and the
      // artifact's ops render under someone else's name. The mount paths
      // re-suffix to avoid this; an artifact that arrives colliding anyway
      // (edited by hand, imported) must at least say so.
      console.warn(`[xray] behaviour artifact "${a.behaviour}" shares its id with an existing feed domain; the existing label wins`);
      continue;
    }
    merged[a.behaviour] = { label: a.manifest?.name || a.behaviour };
  }
  return merged;
}

// A prompt beat keeps enough text to read the intent, not the whole payload;
// a reply beat the same, for the answer. The whole words stay in the turn
// (promptWordsOf, replyWordsOf).
const PROMPT_CAP = 600;
const REPLY_CAP = 700;

/**
 * A user turn's words as its prompt beat reads them, whole: its text blocks
 * without the harness's scaffolding (command stamps, system reminders).
 * `{ text, first }`, `first` the first block that held any; null when none did.
 */
export function promptWordsOf(blocks) {
  let first = -1;
  const parts = [];
  (blocks || []).forEach((b, i) => {
    if (b?.type !== "text" || typeof b.text !== "string") return;
    const t = b.text
      .replace(/<(command-name|command-args|command-message)>[\s\S]*?<\/\1>/g, "")
      // Scaffolding can sit anywhere in the block, not only at its start,
      // and the harness sometimes leaves it unterminated. Strip the tag's
      // span wherever it falls: testing only the block's prefix both drops
      // real text that shares a block with a leading reminder and leaks a
      // trailing reminder into the beat.
      .replace(/<system-reminder>[\s\S]*?<\/system-reminder>/g, "")
      .replace(/<system-reminder>[\s\S]*$/, "")
      .trim();
    if (!t) return;
    if (first < 0) first = i;
    parts.push(t);
  });
  return parts.length ? { text: parts.join("\n\n"), first } : null;
}

/** An assistant turn's words as its reply beat reads them, whole: its text blocks. */
export function replyWordsOf(blocks) {
  let first = -1;
  const parts = [];
  (blocks || []).forEach((b, i) => {
    if (b?.type !== "text" || typeof b.text !== "string") return;
    const t = b.text.trim();
    if (!t) return;
    if (first < 0) first = i;
    parts.push(t);
  });
  return parts.length ? { text: parts.join("\n\n"), first } : null;
}

// Harness-generated user turns that are NOT a person speaking: background
// task notifications, automation events. They still start work (the agent
// reacts to them), but they must not read as user intent — the component
// renders them as minor beats, not episode headers.
const NOTICE_RE = /^\[[^\]\n]*(?:NOTIFICATION|NOT USER INPUT|AUTOMATED)[^\]\n]*\]|^<task-notification\b/i;

// Every step below keys a tool call by its block id: the extractors use it as
// their dedup handle, and the position map resolves it back to (turn, block).
// A block that arrives WITHOUT one breaks both halves at once — the extractors
// fall back to a content hash the position map can't resolve, so the op
// strands at the end of the feed with no turn AND the same block still counts
// as unclassified, double-rendering one call and understating coverage.
//
// Id-less blocks are not hypothetical: parseApiRequest emits `id: null` for
// every Gemini functionCall, and for an Anthropic server web_search whose
// payload carried no id. Mint a stable positional id up front so the
// invariant holds for the whole pipeline. Result pairing is unaffected — a
// block with no id could never be matched to its tool_result anyway.
function withToolUseIds(turns) {
  let patched = false;
  const out = turns.map((turn, turnIndex) => {
    const blocks = turn?.blocks;
    if (!Array.isArray(blocks) || !blocks.some((b) => b?.type === "tool_use" && !b.id)) return turn;
    patched = true;
    return {
      ...turn,
      blocks: blocks.map((b, blockIndex) =>
        b?.type === "tool_use" && !b.id ? { ...b, id: `pos:${turnIndex}:${blockIndex}` } : b),
    };
  });
  return patched ? out : turns;
}

function blockPositions(turns) {
  const pos = new Map();
  (turns || []).forEach((turn, turnIndex) => {
    (turn?.blocks || []).forEach((b, blockIndex) => {
      if (b?.type === "tool_use" && b.id) pos.set(b.id, { turnIndex, blockIndex });
    });
  });
  return pos;
}

// Command-door skill invocations exist only as text markers in user turns —
// the extractor doesn't say where. Re-running the same marker scan in the
// same order yields positions that zip 1:1 with the extractor's cmd-keyed
// invocations.
function cmdMarkerPositions(turns) {
  const list = [];
  (turns || []).forEach((turn, turnIndex) => {
    if (turn?.role !== "user") return;
    (turn.blocks || []).forEach((b, blockIndex) => {
      if (b?.type !== "text" || typeof b.text !== "string") return;
      for (let i = 0; i < commandInvocationsOf(b.text).length; i++) {
        list.push({ turnIndex, blockIndex });
      }
    });
  });
  return list;
}

// items in one residual run → [{ name, n }] most-frequent-first, for the
// interstitial's one-line summary.
export function residualSummary(items) {
  const counts = new Map();
  for (const it of items || []) {
    const name = it.op?.name || "tool";
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, n]) => ({ name, n }))
    .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
}

// One trace's turns → { rows, coverage, domainCounts }.
//   row: { type: "prompt", item }
//      | { type: "ops", domain, items }        — a run of same-domain ops
//      | { type: "residual", items }           — unclaimed tool calls
//   item: { id, domain, turnIndex, blockIndex, op }
//   coverage: { toolUses, claimed, residual }
// One turns array's feed, built once: the lanes model (built again as its
// shell reads land), the reading's draft and the shell reader's claimed keys
// each read the same array's feed, and they read it without changing it (a
// caller copies what it changes). A feed with extra ops is built fresh.
const FEEDS = new WeakMap();

export function buildSemanticFeed(turns, { extraOps = [] } = {}) {
  const cacheable = Array.isArray(turns) && !extraOps?.length;
  if (cacheable && FEEDS.has(turns)) return FEEDS.get(turns);
  const feed = feedOf(turns, extraOps);
  if (cacheable) FEEDS.set(turns, feed);
  return feed;
}

function feedOf(turns, extraOps) {
  const safeTurns = withToolUseIds(Array.isArray(turns) ? turns : []);
  const pos = blockPositions(safeTurns);
  const items = [];
  const claimed = new Set();

  const push = (domain, key, op) => {
    const base = String(key).split("#")[0];
    const p = pos.get(base) || null;
    if (p) claimed.add(base);
    items.push({
      id: `${domain}:${key}`,
      domain,
      op,
      turnIndex: p ? p.turnIndex : null,
      blockIndex: p ? p.blockIndex : 0,
    });
  };

  const fileOps = extractFileOps(safeTurns);
  for (const op of fileOps) push("files", op.key, op);
  // File ops DERIVED from shell/script content — heredoc writes, echo appends,
  // python literal writes, sed transforms replayed against believed state.
  // These carry real content/hunks; the shell-origins pass below only knows
  // THAT a path was written, so where both see the same (call, path) the
  // derived op wins and the shape-only one stands down.
  const derivedOps = extractDerivedFileOps(safeTurns, fileOps);
  const derivedAt = new Set();
  for (const op of derivedOps) {
    push("files", op.key, op);
    derivedAt.add(`${String(op.key).split("#")[0]}|${op.path}`);
  }
  // Files the shell produced, which no tool call ever wrote. Fed the tool-call
  // file ops because a claim has to land on the path exactly as those spell it,
  // or one file reads as two lanes.
  for (const op of extractShellWriteOps(safeTurns, fileOps.concat(derivedOps))) {
    if (derivedAt.has(`${String(op.key).split("#")[0]}|${op.path}`)) continue;
    push("files", op.key, op);
  }
  for (const op of extractWebResearchOps(safeTurns)) push("research", op.key, op);
  for (const op of extractForgeOps(safeTurns)) push("forge", op.key, op);
  for (const op of extractLinearOps(safeTurns)) push("linear", op.key, op);
  for (const op of extractGrafanaOps(safeTurns)) push("grafana", op.key, op);
  for (const op of extractDataQueryOps(safeTurns)) push("query", op.key, op);
  for (const op of extractRecallOps(safeTurns)) push("recall", op.key, op);
  // Code-mode leftovers: cells the unwrap pass (unwrapCodeModeTurns, applied
  // by the caller BEFORE this build) could extract nothing from, plus
  // wait-poll and stdin plumbing. Cells that DID unwrap are claimed through
  // their virtual blocks by the extractors above.
  for (const op of extractCodeCellOps(safeTurns)) push("code", op.key, op);
  // Every manifest lens, in one loop. Adding a lens no longer means adding a line
  // here; an extractor that throws is contained so one bad lens cannot take the
  // whole reading down with it.
  for (const { domain, extract } of xrayRegistry().feedExtractors()) {
    try {
      for (const op of extract(safeTurns) || []) push(domain, op.key, op);
    } catch (err) {
      console.warn(`[xray] ${domain} extractor threw; its work will read as residual`, err);
    }
  }

  // Ops computed OUTSIDE this function — the async tier. Behaviour artifacts
  // execute in the isolated realm, which cannot happen inside a pure sync
  // build, so the caller runs them (cached per trace × artifact version) and
  // re-builds the feed when they land. They claim block ids through the same
  // door as every extractor, so coverage and residual stay one honest number.
  for (const group of extraOps) {
    for (const op of group?.ops || []) push(group.domain, op.key, op);
  }

  const skills = extractSkillSignals(safeTurns);
  const cmdQueue = cmdMarkerPositions(safeTurns);
  let cmdIdx = 0;
  for (const inv of skills.invocations) {
    if (String(inv.key).startsWith("cmd:")) {
      const p = cmdQueue[cmdIdx++] || null;
      items.push({
        id: `skill:${inv.key}:${cmdIdx}`,
        domain: "skill",
        op: inv,
        turnIndex: p ? p.turnIndex : null,
        blockIndex: p ? p.blockIndex : 0,
      });
    } else {
      push("skill", inv.key, inv);
    }
  }

  const artifacts = extractArtifactSignals(safeTurns);
  for (const m of artifacts.marks) push("artifact", m.key, { ...m.publish, key: m.key });

  // Last-resort claim for code-mode cells whose unwrapped work nothing above
  // recognized (a grep, a build, a computed command). The cell is fully READ —
  // source, inner command, output — so leaving it residual would render an
  // interpreted call as an opaque one; it claims into the code domain with the
  // command as its face. Must run AFTER every other claim so it never shadows
  // a real interpretation.
  for (const op of fallbackCodeCellOps(safeTurns, claimed)) push("code", op.key, op);

  // Prompt beats — the episode boundaries of the feed. A user turn carrying a
  // tool_result is the harness returning output, not a person (or driver)
  // speaking; a system-reminder block is harness scaffolding; command stamps
  // are the slash-command door (already surfaced as skill ops above). What's
  // left is the closest cheap read on "the intent that started this stretch".
  safeTurns.forEach((turn, turnIndex) => {
    if (turn?.role !== "user") return;
    const blocks = turn.blocks || [];
    if (blocks.some((b) => b?.type === "tool_result")) return;
    const words = promptWordsOf(blocks);
    if (!words) return;
    const { text } = words;
    items.push({
      id: `prompt:${turnIndex}`,
      domain: "prompt",
      turnIndex,
      blockIndex: words.first,
      op: {
        kind: NOTICE_RE.test(text) ? "notice" : "user",
        text: text.length > PROMPT_CAP ? text.slice(0, PROMPT_CAP) : text,
        truncated: text.length > PROMPT_CAP,
      },
    });
  });

  // Reply beats — an episode's OUTCOME. Without these the feed shows two
  // prompts back to back and hides what the second one is answering: for a
  // session whose product IS prose (an assessment, a recommendation, a review
  // verdict) that means the feed reads as empty work.
  //
  // Exactly one per episode, and only a genuine conclusion: scanning back
  // from the episode's end, the first assistant turn found must carry text
  // and NO tool calls — a turn that mixes prose with tool calls is narrating
  // work in progress, so the episode simply has no outcome yet.
  const promptTurns = items
    .filter((it) => it.domain === "prompt" && it.op.kind === "user" && it.turnIndex != null)
    .map((it) => it.turnIndex)
    .sort((a, b) => a - b);
  const spans = [];
  if (!promptTurns.length) spans.push([0, safeTurns.length]);
  else {
    if (promptTurns[0] > 0) spans.push([0, promptTurns[0]]);
    promptTurns.forEach((t, i) => {
      spans.push([t, i + 1 < promptTurns.length ? promptTurns[i + 1] : safeTurns.length]);
    });
  }
  for (const [start, end] of spans) {
    for (let ti = end - 1; ti >= start; ti--) {
      const turn = safeTurns[ti];
      if (turn?.role !== "assistant") continue;
      const blocks = turn.blocks || [];
      if (blocks.some((b) => b?.type === "tool_use")) break;
      const words = replyWordsOf(blocks);
      if (!words) continue;
      const { text } = words;
      items.push({
        id: `reply:${ti}`,
        domain: "reply",
        turnIndex: ti,
        blockIndex: words.first,
        op: {
          text: text.length > REPLY_CAP ? text.slice(0, REPLY_CAP) : text,
          truncated: text.length > REPLY_CAP,
        },
      });
      break;
    }
  }

  // Residual — every tool_use no extractor claimed. Kept per-block (not
  // pre-summarized) so runs group below like any other domain.
  let toolUses = 0;
  safeTurns.forEach((turn, turnIndex) => {
    (turn?.blocks || []).forEach((b, blockIndex) => {
      if (b?.type !== "tool_use") return;
      // Virtual blocks are DERIVED from a code-mode cell, not calls of their
      // own: the parent cell is the countable call, and it is claimed when any
      // of its virtual children is. Counting them would inflate toolUses and
      // list every unclaimed derivation as residual beside its parent.
      if (b.virtual) return;
      toolUses += 1;
      if (b.id && claimed.has(b.id)) return;
      items.push({
        id: `other:${b.id || `${turnIndex}:${blockIndex}`}`,
        domain: "other",
        turnIndex,
        blockIndex,
        op: { name: String(b.name || "tool") },
      });
    });
  });

  items.sort(
    (a, b) =>
      (a.turnIndex ?? Infinity) - (b.turnIndex ?? Infinity) ||
      a.blockIndex - b.blockIndex,
  );

  const rows = [];
  const domainCounts = {};
  for (const item of items) {
    domainCounts[item.domain] = (domainCounts[item.domain] || 0) + 1;
    if (item.domain === "prompt" || item.domain === "reply") {
      rows.push({ type: item.domain, item });
      continue;
    }
    const last = rows[rows.length - 1];
    if (item.domain === "other") {
      if (last?.type === "residual") last.items.push(item);
      else rows.push({ type: "residual", items: [item] });
      continue;
    }
    if (last?.type === "ops" && last.domain === item.domain) last.items.push(item);
    else rows.push({ type: "ops", domain: item.domain, items: [item] });
  }

  return {
    rows,
    domainCounts,
    coverage: { toolUses, claimed: claimed.size, residual: toolUses - claimed.size },
    // WHICH calls the committed extractors claimed, not just how many. The
    // shell door defers to them per call: two readings of one call put two ops
    // on one action, which is the same defect a wrong op key produces.
    claimedKeys: claimed,
  };
}

// The turn span of a row, for its "T n–m" chip. Null-position items don't
// shrink the span; an all-null row yields null.
export function rowTurnSpan(row) {
  const idxs = (row?.items ? row.items : [row?.item]).map((it) => it?.turnIndex).filter((v) => v != null);
  if (!idxs.length) return null;
  return { start: Math.min(...idxs), end: Math.max(...idxs) };
}
